import { Metamodel } from 'app/model/base/model';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { LinkedService, Pipeline } from 'shell-models-lib';

/**
 * Calculates whether a pipeline (and its nested container activities) depend on
 * SAP CDC / SAP ODP connectors, and sets `showSapCdcWarning` accordingly.
 *
 * Extracted as a standalone utility so it can be called both at postToModel time
 * (initial load) and on modelChanged$ (edit-time re-trigger).
 */
export function calcSapCdcWarning(pipeline: Pipeline, visited: Set<Pipeline> = new Set()): void {
    if (!pipeline || visited.has(pipeline)) {
        return;
    }
    visited.add(pipeline);
    pipeline.showSapCdcWarning = false;
    const sapCdcTypes = [
        adfModelTypeMap.sapOdp.linkedServiceType,
        adfModelTypeMap.sapCdc.linkedServiceType
    ];
    for (const activity of pipeline.activities) {
        const act = activity as {
            sourceDataset?: { linkedService?: LinkedService };
            sinkDataset?: { linkedService?: LinkedService };
            linkedService?: LinkedService;
            dataset?: { linkedService?: LinkedService };
            pipeline?: Pipeline;
            defaultPipeline?: Pipeline;
            cases?: Array<{ casePipeline?: Pipeline }>;
            ifFalseActivityPipeline?: Pipeline;
            ifTrueActivityPipeline?: Pipeline;
            dataflow?: { hasSapCdcDependency?: boolean };
        };
        const ls = act.sourceDataset?.linkedService
            || act.sinkDataset?.linkedService
            || act.linkedService
            || act.dataset?.linkedService;
        if (ls && sapCdcTypes.includes(Metamodel.modelOf(ls).name)) {
            pipeline.showSapCdcWarning = true;
            return;
        }
        // ExecuteDataFlow activity: propagate from the referenced data flow's sources.
        if (act.dataflow?.hasSapCdcDependency) {
            pipeline.showSapCdcWarning = true;
            return;
        }
        // Recurse into nested container activities
        if (act.pipeline) {
            calcSapCdcWarning(act.pipeline, visited);
            pipeline.showSapCdcWarning = act.pipeline.showSapCdcWarning;
        } else if (act.defaultPipeline) {
            calcSapCdcWarning(act.defaultPipeline, visited);
            pipeline.showSapCdcWarning = act.defaultPipeline.showSapCdcWarning;
            if (!pipeline.showSapCdcWarning && act.cases) {
                for (const caseItem of act.cases) {
                    calcSapCdcWarning(caseItem.casePipeline, visited);
                    if (caseItem.casePipeline?.showSapCdcWarning) {
                        pipeline.showSapCdcWarning = true;
                        break;
                    }
                }
            }
        } else if (act.ifFalseActivityPipeline || act.ifTrueActivityPipeline) {
            if (act.ifFalseActivityPipeline) {
                calcSapCdcWarning(act.ifFalseActivityPipeline, visited);
                pipeline.showSapCdcWarning = act.ifFalseActivityPipeline.showSapCdcWarning;
            }
            if (!pipeline.showSapCdcWarning && act.ifTrueActivityPipeline) {
                calcSapCdcWarning(act.ifTrueActivityPipeline, visited);
                pipeline.showSapCdcWarning = act.ifTrueActivityPipeline.showSapCdcWarning;
            }
        }
        if (pipeline.showSapCdcWarning) {
            return;
        }
    }
}
